import { Button, SkeletonText } from '@ashniva/ui';
import { useState } from 'react';

import { useRiFileContentQuery } from '../api-code';
import { PAGE_LINES, pageStart, splitLines } from '../code-window';
import { shortCommit } from '../freshness';
import { linesLabel } from '../ri-text';
import { CodeViewer } from './CodeViewer';
import { ProblemNotice } from './ProblemNotice';

/** Lines shown around a citation in the side panel. */
const CONTEXT_LINES = 30;

interface FileViewerProps {
  linkId: string;
  path: string;
  /** Lines to highlight and bring into view. */
  focus?: { start: number; end: number } | null;
  /** `page`: the file 2 000 lines at a time (the Files tab); `around`: the focus with some context (a citation). */
  mode?: 'page' | 'around';
  lineHref?: (line: number) => string;
}

/** One file of the indexed snapshot, read from Desk a page at a time and shown as plain text (P14-05). */
export function FileViewer({
  linkId,
  path,
  focus = null,
  mode = 'page',
  lineHref,
}: FileViewerProps) {
  const focusKey = `${path}:${focus?.start ?? 1}`;
  const [paged, setPaged] = useState<{ key: string; start: number } | null>(null);
  const defaultStart =
    mode === 'around'
      ? Math.max(1, (focus?.start ?? 1) - CONTEXT_LINES)
      : pageStart(focus?.start ?? 1);
  const startLine = paged?.key === focusKey ? paged.start : defaultStart;
  const endLine =
    mode === 'around' ? (focus?.end ?? 1) + CONTEXT_LINES : startLine + PAGE_LINES - 1;
  const content = useRiFileContentQuery(linkId, path, startLine, endLine);

  if (content.isLoading) {
    return (
      <div aria-busy="true">
        <span className="sr-only">Loading {path}</span>
        <SkeletonText lines={8} />
      </div>
    );
  }
  if (content.isError || !content.data) {
    return <ProblemNotice error={content.error} onRetry={() => void content.refetch()} size="sm" />;
  }
  const file = content.data;
  const lines = splitLines(file.content);
  const goTo = (start: number) => setPaged({ key: focusKey, start });
  return (
    <div className="ri-stack">
      <div className="ri-row ri-muted">
        <span className="ri-mono">{file.path}</span>
        <span>
          {linesLabel(file.startLine, file.endLine)} of {file.totalLines}
        </span>
        {file.commit ? <span>at commit {shortCommit(file.commit)}</span> : null}
        {file.language ? <span>{file.language}</span> : null}
      </div>
      {file.redactions > 0 ? (
        <p className="ri-muted">
          {file.redactions === 1
            ? 'One value that looked like a secret was'
            : `${file.redactions} values that looked like secrets were`}{' '}
          hidden before the code reached Desk.
        </p>
      ) : null}
      <CodeViewer
        label={file.path}
        lines={lines}
        firstLine={file.startLine}
        highlight={focus}
        focusLine={focus?.start ?? null}
        lineHref={lineHref}
      />
      {mode === 'page' && (file.startLine > 1 || file.endLine < file.totalLines) ? (
        <div className="ri-row">
          <Button
            size="sm"
            disabled={file.startLine <= 1}
            onClick={() => goTo(Math.max(1, file.startLine - PAGE_LINES))}
          >
            Previous {PAGE_LINES} lines
          </Button>
          <Button
            size="sm"
            disabled={file.endLine >= file.totalLines}
            onClick={() => goTo(file.endLine + 1)}
          >
            Next {PAGE_LINES} lines
          </Button>
        </div>
      ) : null}
    </div>
  );
}
